import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Instagram, Youtube, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" } })
};

const SOCIAL_LINKS = [
  { icon: Instagram, label: "Instagram", href: "https://instagram.com/BadCompany_Entertainment", handle: "@BadCompany_Entertainment" },
  { icon: Youtube, label: "YouTube", href: "https://youtube.com/@BadCompanyEntertainmentTV", handle: "BadCompanyEntertainmentTV" },
  { icon: Mail, label: "Email", href: "mailto:info@badcompanyentertainment.com", handle: "info@badcompanyentertainment.com" },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, form);
      toast.success("Message sent! We'll be in touch.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Failed to send message. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div data-testid="contact-page">
      {/* ── HERO ── */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 noise-overlay relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Contact</p>
          </motion.div>
          <motion.h1
            initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] uppercase tracking-tighter leading-[0.9] mb-6"
          >
            Let's Connect
          </motion.h1>
          <motion.p
            initial="hidden" animate="visible" variants={fadeUp} custom={2}
            className="font-body text-base lg:text-lg text-zinc-400 max-w-xl leading-relaxed"
          >
            Let's build something meaningful together.
          </motion.p>
        </div>
      </section>

      {/* ── FORM + INFO ── */}
      <section className="py-24 lg:py-32 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-subheading text-2xl uppercase tracking-tight mb-8">Send a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Name</label>
                  <input
                    data-testid="contact-name"
                    required
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    className="bc-input w-full"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Email</label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    className="bc-input w-full"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Message</label>
                  <textarea
                    data-testid="contact-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    className="bc-input w-full resize-none"
                    placeholder="How can we work together?"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={submitting}
                  className="font-subheading text-sm uppercase tracking-widest bg-white text-black px-8 py-3.5 hover:bg-zinc-200 transition-colors duration-300 disabled:opacity-50 flex items-center gap-2"
                >
                  <Send size={14} />
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </motion.div>

            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:pl-8"
            >
              <h2 className="font-subheading text-2xl uppercase tracking-tight mb-8">Info</h2>

              {/* Location */}
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 border border-white/20 flex items-center justify-center">
                    <MapPin size={16} className="text-zinc-400" />
                  </div>
                  <div>
                    <p className="font-subheading text-xs uppercase tracking-wider text-zinc-500">Location</p>
                    <p className="font-body text-sm text-zinc-300">Las Vegas, Nevada</p>
                  </div>
                </div>
              </div>

              {/* Social */}
              <div>
                <p className="font-subheading text-xs uppercase tracking-wider text-zinc-500 mb-6">Follow Us</p>
                <div className="space-y-6">
                  {SOCIAL_LINKS.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-social-${social.label.toLowerCase()}`}
                      className="flex items-center gap-4 group"
                    >
                      <div className="w-10 h-10 border border-white/20 flex items-center justify-center group-hover:border-white/40 transition-colors duration-300">
                        <social.icon size={16} className="text-zinc-400 group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <p className="font-subheading text-xs uppercase tracking-wider text-zinc-500">{social.label}</p>
                        <p className="font-body text-sm text-zinc-300 group-hover:text-white transition-colors">{social.handle}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Additional */}
              <div className="mt-12 border border-white/10 p-6">
                <p className="font-subheading text-xs uppercase tracking-wider text-zinc-500 mb-2">Sponsorship Inquiries</p>
                <p className="font-body text-sm text-zinc-400 mb-4">
                  For sponsorship and partnership opportunities, reach out directly.
                </p>
                <a
                  href="mailto:sponsors@badcompanyentertainment.com"
                  data-testid="contact-sponsor-email"
                  className="font-subheading text-xs uppercase tracking-widest text-white border-b border-white/30 pb-1 hover:border-white transition-colors"
                >
                  sponsors@badcompanyentertainment.com
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
